import React, { useContext, useMemo } from 'react';
import { t } from 'i18next';

import { CurrenciesContext } from '@/currencies/currency.context';
import { SubscriptionFormParams } from './useSubscribeAssetForm.hook';
import { formatDate } from '../../../../packages/utils/dateFormatting';
import { formatAmountByDecimals } from '../../../../packages/utils/numberFormatting';
import { ProgressStatus } from '@/design-system/components/progress/ProgressItem';
import { GetWalletsByAssetResponseWalletsItem, UserEarnDetailResponse, useUtxEarnSubscriptionsGetPreview } from '@utx/utx-client';
import { UniTextDetails } from '@/design-system/components/cards';
import { View } from 'dripsy';
import { TextBody } from '@/design-system/components/typography';

export type SubscriptionPreview = {
  id: string;
  asset: string;
  rewardAsset: string;
  subscriptionDate: string;
  firstRewardAccrualDate: string;
  rewardCommissionPercent: number;
  estimatedRewardsAfterMaturity: number;
  maturityDate?: string;
  autoRenewable: boolean;
};

interface UseSubscriptionPreviewParams {
  account: GetWalletsByAssetResponseWalletsItem;
  requestParams: Required<SubscriptionFormParams>;
  offerDetails?: UserEarnDetailResponse;
}

export const useSubscriptionPreview = (params: UseSubscriptionPreviewParams) => {
  const { account, requestParams, offerDetails } = params;
  const { getDecimalByAsset } = useContext(CurrenciesContext);

  const query = useUtxEarnSubscriptionsGetPreview({
    mutation: {
      retry: 0
    }
  });

  const progressCardData = useMemo(() => {
    if (!query.data) return [];
    const data = query.data.data as SubscriptionPreview;
    if (!data) return [];

    let result: Array<ProgressStatus> = [
      {
        name: t('earn-invest::glossary.subscription'),
        date: formatDate(data.subscriptionDate),
        status: 'inactive',
        noFormat: true
      },
      {
        name: t('earn-invest::glossary.first-reward'),
        date: formatDate(data.firstRewardAccrualDate),
        status: 'inactive',
        noFormat: true
      }
    ];

    if (data.maturityDate) {
      result.push({
        name: t('earn-invest::glossary.maturity'),
        date: formatDate(data.maturityDate),
        status: 'inactive',
        noFormat: true
      });
    }

    return result;
  }, [query.data]);

  const textDetailsCardData: Array<UniTextDetails> = useMemo(() => {
    const data = query.data?.data as SubscriptionPreview;
    const isFlexible = offerDetails?.tenor === null;
    if (!data || isFlexible) return [];

    const status: 'default' | 'success' | 'danger' = 'success';

    const content: Array<UniTextDetails> = [
      {
        name: t('earn-invest::glossary.account'),
        data: '',
        status: 'default',
        render: () => {
          return (
            <View sx={{ alignItems: 'flex-end' }}>
              <TextBody>
                {account.accountLabel ? t(`earn-invest::glossary.${account.accountLabel}`) : account.accountName || ' - '}
              </TextBody>
              <TextBody>{account.accountNumber}</TextBody>
            </View>
          );
        }
      }
    ];

    if (data.autoRenewable) {
      content.push({
        name: t('earn-invest::glossary.auto-renewal'),
        subText: '',
        data: requestParams?.autoRenew ? t('earn-invest::glossary.enabled') : t('earn-invest::glossary.disabled')
      });
    }

    return content;
  }, [query.data]);

  return {
    ...query,
    progressCardData,
    textDetailsCardData,
    estimatedRewardsAfterMaturity: query.data?.data?.estimatedRewardsAfterMaturity
  };
};
